/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'react-i18next';
import {
  useFetchReportList,
  useDownloadPresignedUrl,
  useFetchCustomReportList,
  useMonthlyReportGeneration,
  useCustomerMonthlyPdf,
  useRegenerateMonthlyReport,
  useSubmitSapMonthlyReport,
} from './Hook/Index';
import { useOperatorStore } from '../../store/useOperatorStore';
import TouInlineEditor from '../tou-pricing/TouInlineEditor';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Tabs,
  DatePicker,
  Table,
  Button,
  Grid,
  Tag,
  Empty,
  Select,
  Drawer,
  Form,
  InputNumber,
  message,
  Upload,
  Typography,
  Space,
  Tooltip,
} from 'antd';
import {
  UserOutlined,
  ThunderboltOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ReloadOutlined,
  PlusOutlined,
  LineChartOutlined,
  TeamOutlined,
  PoweroffOutlined,
  UserSwitchOutlined,
  CloudUploadOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { formatKwhFromWatts, formatReportNumber } from '../../utils/CommonFunctions';
import { useAuthStore } from '../../store/useAuthStore';
import { useFilUpload } from '../../Hooks/Index';
import { DebouncedSelect, fetchCustomerList } from '../../components/useDebouncedSelectLocation';
import ChargePointCountTab from './ChargePointCountTab';
import ChargingSessionTab from './ChargingSessionTab';
import EnergyConsumptionTab from './EnergyConsumptionTab';
import ChargerOnlineTimeTab from './ChargerOnlineTimeTab';
import UserCountTab from './UserCountTab';
import UserAnalysisTab from './UserAnalysisTab';
import CustomerDrawerView from '../../components/CustomerDrawerView';
import { getErrorMessage } from '../../utils/errorHandler';
import { currency } from '../../utils/utils';

const { MonthPicker, RangePicker } = DatePicker;
const { useBreakpoint } = Grid;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

function CreateReportStep({ step, title, description, extra, children }) {
  return (
    <div className="mb-3 rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          <div
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
            style={{ background: 'var(--ant-color-primary)' }}
          >
            {step}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-gray-900">{title}</div>
            {description ? (
              <div className="text-xs text-gray-500">{description}</div>
            ) : null}
          </div>
        </div>
        {extra ? (
          <div className="flex shrink-0 items-center gap-2 pt-0.5">{extra}</div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export default function ReportPage() {
  const { t } = useTranslation('reportPage');
  const { user } = useAuthStore();
  const { operatorId, name: operatorName } = useOperatorStore();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [createReportDrawerOpen, setCreateReportDrawerOpen] = useState(false);
  const [createReportForm] = Form.useForm();
  const [createReportBillFileList, setCreateReportBillFileList] = useState([]);
  const [createReportUploadedBillUrl, setCreateReportUploadedBillUrl] = useState('');
  const [touToolbar, setTouToolbar] = useState(null);
  const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false);
  const [selectedCustomerIdForDrawer, setSelectedCustomerIdForDrawer] = useState(null);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const lastMonth = dayjs().subtract(1, 'month');
  const [createReportMonth, setCreateReportMonth] = useState(lastMonth);
  const createTouYear = createReportMonth?.year?.() ?? lastMonth.year();
  const createTouMonth = createReportMonth
    ? createReportMonth.month() + 1
    : lastMonth.month() + 1;
  const [touCoverage, setTouCoverage] = useState({
    isComplete: false,
    status: 'incomplete',
    isLoading: true,
    isSaving: false,
    dirty: false,
  });
  const handleTouCoverageChange = useCallback((next) => {
    setTouCoverage((prev) => {
      if (
        prev.isComplete === next.isComplete &&
        prev.status === next.status &&
        prev.isLoading === next.isLoading &&
        prev.isSaving === next.isSaving &&
        prev.dirty === next.dirty
      ) {
        return prev;
      }
      return next;
    });
  }, []);
  const isTouComplete = !!touCoverage.isComplete && !touCoverage.dirty && !touCoverage.isSaving;
  const canCreateReport =
    !!operatorId &&
    !touCoverage.isLoading &&
    isTouComplete &&
    !!createReportMonth;
  const createReportDisabledReason = !operatorId
    ? t('selectOperatorFirst')
    : !createReportMonth
      ? t('selectMonthFirst')
      : touCoverage.isLoading
        ? t('loadingTouRates')
        : touCoverage.isSaving || touCoverage.dirty
          ? t('touSaveBeforeCreate')
          : !touCoverage.isComplete
            ? t('touNotComplete', { month: createReportMonth.format('MMM YYYY') })
            : '';
  const [selectedRole, setSelectedRole] = useState(() => {
    if (['SUPER_ADMIN', 'ADMIN', 'OPERATOR'].includes(user?.role)) return 'OPERATOR';
    return 'CUSTOMER';
  });
  const [reportCustomerId, setReportCustomerId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [activeMainTab, setActiveMainTab] = useState(
    location.state?.activeMainTab || 'otherReports'
  );
  const [activeReportTab, setActiveReportTab] = useState(() => {
    if (['SUPER_ADMIN', 'ADMIN', 'OPERATOR'].includes(user?.role)) return 'OPERATOR';
    return 'CUSTOMER';
  });
  const [activeOtherReportTab, setActiveOtherReportTab] = useState(
    location.state?.activeOtherReportTab || 'chargePointCount'
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [customReportPagination, setCustomReportPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Custom Report Filters
  const [customReportDateRange, setCustomReportDateRange] = useState([]);
  const [customReportStatus, setCustomReportStatus] = useState(undefined);
  const [customReportTemplate, setCustomReportTemplate] = useState(undefined);
  const [downloadingCustomReportId, setDownloadingCustomReportId] = useState(null);

  const customReportPayload = useMemo(() => ({
    ...(operatorId && { operatorId }),
    offset: customReportPagination.current,
    limit: customReportPagination.pageSize,
    page: customReportPagination.current,
    ...(customReportDateRange?.[0] && { fromDate: customReportDateRange[0].toISOString() }),
    ...(customReportDateRange?.[1] && { toDate: customReportDateRange[1].toISOString() }),
    ...(customReportStatus && { status: customReportStatus }),
    ...(customReportTemplate && { reportTemplate: customReportTemplate }),
  }), [operatorId, customReportPagination, customReportDateRange, customReportStatus, customReportTemplate]);

  const { mutate: DownloadFile } = useDownloadPresignedUrl();
  const { mutateAsync: uploadFile } = useFilUpload();
  const { mutate: generateMonthlyReport, isPending: isGeneratingReport } = useMonthlyReportGeneration({
    onSuccess: (_data, variables) => {
      message.success(t('createReportSuccess'));
      setCreateReportDrawerOpen(false);
      createReportForm.resetFields();
      setCreateReportBillFileList([]);
      setCreateReportUploadedBillUrl('');
      if (variables?.reportYear && variables?.reportMonth) {
        const ym = `${variables.reportYear}-${String(variables.reportMonth).padStart(2, '0')}`;
        setSelectedMonth(ym);
        setPagination((prev) => ({ ...prev, current: 1 }));
      }
      queryClient.invalidateQueries({ queryKey: ['REPORT_LIST'] });
    },
    onError: (err) => {
      message.error(err?.response?.data?.message || t('somethingWentWrong'));
    },
  });

  const { mutate: regenerateMonthlyReport, isPending: isRegeneratingReport } =
    useRegenerateMonthlyReport({
      onSuccess: () => {
        message.success(t('regenerateReportSuccess'));
        queryClient.invalidateQueries({ queryKey: ['REPORT_LIST'] });
      },
      onError: (err) => {
        message.error(getErrorMessage(err, t('somethingWentWrong')));
      },
    });

  const { mutate: submitSapMonthlyReport, isPending: isSubmittingSap } =
    useSubmitSapMonthlyReport({
      onSuccess: () => {
        message.success(t('submitSapSuccess'));
        queryClient.invalidateQueries({ queryKey: ['REPORT_LIST'] });
      },
      onError: (err) => {
        message.error(getErrorMessage(err, t('submitSapFailed')));
      },
    });

  const [actionReportId, setActionReportId] = useState(null);

  const { mutate: generateCustomerPdf, isPending: isGeneratingCustomerPdf } = useCustomerMonthlyPdf({
    onSuccess: () => {
      message.success(t('customerPdfSuccess'));
      queryClient.invalidateQueries({ queryKey: ['REPORT_LIST'] });
    },
    onError: (err) => {
      message.error(err?.response?.data?.message || t('somethingWentWrong'));
    },
  });

  const handleGenerateCustomerPdf = () => {
    if (!reportCustomerId) {
      message.warning(t('selectCustomerFirst'));
      return;
    }
    if (!selectedMonth) {
      message.warning(t('selectMonthFirst'));
      return;
    }
    const m = dayjs(selectedMonth);
    generateCustomerPdf({
      customerId: reportCustomerId,
      year: m.year(),
      month: m.month() + 1,
    });
  };

  const getSelectedMonthParams = (month) => {
    const m = dayjs(month);
    return {
      reportYear: m.year(),
      reportMonth: m.month() + 1,
    };
  };

  const { data: reportList, isLoading: isReportListLoading } =
    useFetchReportList(
      {
        role: selectedRole,
        ...(selectedRole !== 'SUPER_ADMIN' && { operatorId }),
        ...(selectedMonth && getSelectedMonthParams(selectedMonth)),
        ...(selectedRole === 'CUSTOMER' && reportCustomerId && { customerId: reportCustomerId }),
        offset: pagination.current,
        limit: pagination.pageSize,
      },
      {
        enabled:
          activeMainTab === 'reports' &&
          Object.keys(selectedMonth || {}).length > 0,
        refetchInterval: (query) => {
          const rows = query.state.data?.paginatedData || [];
          const busy = rows.some(
            (r) => r?.status === 'PROCESSING' || r?.status === 'PENDING'
          );
          return busy ? 3000 : false;
        },
      }
    );
  const { data: customReportList, isLoading: isCustomReportListLoading, refetch: refetchCustomReportList } =
    useFetchCustomReportList(
      customReportPayload,
      {
        enabled: activeMainTab === 'customReports',
      }
    );

  const handleMainTabChange = (key) => {
    setActiveMainTab(key);
  };

  const handleReportsTabChange = (key) => {
    setSelectedRole(key);
    setActiveReportTab(key);
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (key !== 'CUSTOMER') setReportCustomerId(null);
  };

  const handleOtherReportsTabChange = (key) => {
    setActiveOtherReportTab(key);
  };

  const handleMonthChange = (date, dateString) => { setSelectedMonth(dateString || (date && dayjs(date).format('YYYY-MM'))) };

  const handleTableChange = (pagination) => setPagination(pagination);

  const handleOpenCreateReport = () => {
    const defaultMonth = dayjs().subtract(1, 'month');
    setCreateReportMonth(defaultMonth);
    setTouCoverage({
      isComplete: false,
      status: 'incomplete',
      isLoading: true,
      isSaving: false,
      dirty: false,
    });
    createReportForm.setFieldsValue({
      reportMonth: defaultMonth,
      fullBillAmount: undefined,
    });
    setCreateReportBillFileList([]);
    setCreateReportUploadedBillUrl('');
    setTouToolbar(null);
    setCreateReportDrawerOpen(true);
  };

  const handleCloseCreateReportDrawer = () => {
    setCreateReportDrawerOpen(false);
    createReportForm.resetFields();
    setCreateReportBillFileList([]);
    setCreateReportUploadedBillUrl('');
    setTouToolbar(null);
    setCreateReportMonth(dayjs().subtract(1, 'month'));
    setTouCoverage({
      isComplete: false,
      status: 'incomplete',
      isLoading: true,
      isSaving: false,
      dirty: false,
    });
  };

  const handleBillFileUpload = async ({ file, onSuccess, onError }) => {
    try {
      const url = await uploadFile(file);
      const resolvedUrl = typeof url === 'string' ? url : url?.url ?? url?.Location;
      setCreateReportUploadedBillUrl(resolvedUrl);
      setCreateReportBillFileList((prev) =>
        prev.filter((f) => f.uid !== file.uid).concat({ uid: file.uid, name: file.name, status: 'done', url: resolvedUrl })
      );
      onSuccess(resolvedUrl, file);
      message.success(t('billFileUploadSuccess'));
    } catch (err) {
      onError(err);
      message.error(err?.response?.data?.message || t('billFileUploadFailed'));
    }
  };

  const handleCreateReportSubmit = (values) => {
    const resolvedOperatorId = values.operatorId || operatorId;
    if (!resolvedOperatorId) {
      message.warning(t('selectOperatorFirst'));
      return;
    }
    const monthValue = values.reportMonth
      ? dayjs(values.reportMonth)
      : createReportMonth;
    if (!monthValue || !monthValue.isValid()) {
      message.warning(t('selectMonthFirst'));
      return;
    }
    if (monthValue.isAfter(dayjs(), 'month')) {
      message.warning(t('cannotCreateFutureMonth'));
      return;
    }
    const payload = {
      operatorId: resolvedOperatorId,
      reportYear: monthValue.year(),
      reportMonth: monthValue.month() + 1,
    };
    if (values.fullBillAmount != null && values.fullBillAmount !== '') {
      payload.fullBillAmount = values.fullBillAmount;
    }
    if (createReportUploadedBillUrl) {
      payload.billFileUrl = createReportUploadedBillUrl;
    }
    generateMonthlyReport(payload);
  };


  const handleDownloadReport = (fileUrl, reportName) => {
    DownloadFile(
      { fileUrl },
      {
        onSuccess: (data) => {
          console.log("downloaded report", data);
          const link = document.createElement('a');
          link.href = data.data;
          link.setAttribute('download', reportName);
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        onError: () => console.error('Failed to download the report.'),
      }
    );
  };

  const handleOpenBillFile = (fileUrl) => {
    DownloadFile(
      { fileUrl },
      {
        onSuccess: (data) => {
          window.open(data.data, '_blank', 'noopener,noreferrer');
        },
        onError: (error) => message.error(getErrorMessage(error, t('downloadFailed'))),
      }
    );
  };

  const handleDownloadCustomReport = (record) => {
    const fileUrl = record.excelReportPath || record.reportPath;
    if (!fileUrl) return;
    const isExcel = !!record.excelReportPath || String(fileUrl).toLowerCase().endsWith('.xlsx');
    const ext = isExcel ? 'xlsx' : 'pdf';
    const reportName = `${record.name || 'custom-report'}.${ext}`;
    setDownloadingCustomReportId(record._id);
    DownloadFile(
      { fileUrl },
      {
        onSuccess: (data) => {
          const link = document.createElement('a');
          link.href = data.data;
          link.setAttribute('download', reportName);
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        onError: (error) => message.error(getErrorMessage(error, t('downloadFailed'))),
        onSettled: () => setDownloadingCustomReportId(null),
      }
    );
  };

  const commonColumns = [
    {
      title: t('range'),
      key: 'dateRange',
      align: 'center',
      width: isMobile ? 140 : 180,
      render: (_, record) => {
        const from = record.from != null ? Number(record.from) : null;
        const to = record.to != null ? Number(record.to) : null;
        if (from == null || to == null) return '—';
        try {
          const f = dayjs.unix(from);
          const tDate = dayjs.unix(to);
          if (f.month() === tDate.month() && f.year() === tDate.year()) {
            return `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
          }
          if (f.year() === tDate.year()) {
            return `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
          }
          return `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
        } catch {
          return '—';
        }
      },
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: isMobile ? 100 : 110,
      render: (status) => {
        const statusConfig = {
          COMPLETED: { color: 'green' },
          PENDING: { color: 'orange' },
          PROCESSING: { color: 'blue' },
          IN_PROGRESS: { color: 'blue' },
          FAILED: { color: 'red' },
        };
        const config = statusConfig[status] || { color: 'default' };
        const label =
          status === 'PROCESSING' ? t('PROCESSING') || t('IN_PROGRESS') : t(status) || status;
        return <Tag color={config.color} variant="outlined">{label}</Tag>;
      },
    },
    {
      title: t('chargingConsumption'),
      dataIndex: 'totalConsumption',
      key: 'totalConsumption',
      align: 'center',
      width: isMobile ? 120 : 140,
      render: (_, record) => `${formatKwhFromWatts(record?.totalConsumption)} kWh`,
    },
    // Electricity bill columns: show "—" when electricityBillDetails is missing (existing reports unchanged)
    {
      title: t('electricityBillConsumption'),
      key: 'electricityBillConsumption',
      align: 'center',
      width: isMobile ? 120 : 140,
      render: (_, record) => {
        const bill = record?.electricityBillDetails;
        const val = bill?.totalConsumption;
        if (!bill || val == null || val === '' || Number.isNaN(Number(val))) return '—';
        return `${formatReportNumber(val)} kWh`;
      },
    },
    {
      title: t('electricityBillAmount'),
      key: 'electricityBillAmount',
      align: 'center',
      width: isMobile ? 100 : 120,
      render: (_, record) => {
        const bill = record?.electricityBillDetails;
        if (!bill || bill.totalAmount == null || bill.totalAmount === '') return '—';
        return formatReportNumber(bill.totalAmount);
      },
    },
    {
      title: t('perUnitPrice'),
      key: 'electricityBillPerUnitPrice',
      align: 'center',
      width: isMobile ? 100 : 110,
      render: (_, record) => {
        const bill = record?.electricityBillDetails;
        if (!bill || bill.perUnitPrice == null || bill.perUnitPrice === '') return '—';
        return formatReportNumber(bill.perUnitPrice);
      },
    },
    {
      title: t('electricityBillFile'),
      key: 'electricityBillFile',
      align: 'center',
      width: isMobile ? 90 : 100,
      render: (_, record) => {
        const bill = record?.electricityBillDetails;
        const fileUrl = bill?.billFileUrl;
        if (!fileUrl || typeof fileUrl !== 'string') return '—';
        return (
          <Tooltip title={t('downloadReport')}>
            <Button
              variant="outlined"
              icon={<DownloadOutlined />}
              onClick={() => handleOpenBillFile(fileUrl)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: t('reportLink'),
      key: 'reportPath',
      align: 'center',
      width: isMobile ? 100 : 120,
      render: (_, record) => {
        const fileUrl = record.excelReportPath || record.reportPath;
        const isExcel = record.excelReportPath || (record.reportPath && String(record.reportPath).toLowerCase().endsWith('.xlsx'));
        const ext = isExcel ? 'xlsx' : 'pdf';
        if (!fileUrl) return null;
        return (
          <Tooltip title={t('downloadReport')}>
            <Button
              variant="outlined"
              icon={<DownloadOutlined />}
              onClick={() =>
                handleDownloadReport(fileUrl, `${record.name || 'report'}.${ext}`)
              }
            />
          </Tooltip>
        );
      },
    },
  ];

  const operatorColumns = [
    {
      title: t('reportName'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
    {
      title: t('operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
    ...commonColumns.filter((col) => col.key !== 'reportPath'),
    {
      title: t('downloadExcel'),
      key: 'downloadExcel',
      align: 'center',
      width: isMobile ? 90 : 100,
      render: (_, record) => {
        const fileUrl = record.excelReportPath || record.reportPath;
        if (!fileUrl || record.status !== 'COMPLETED') return '—';
        return (
          <Tooltip title={t('downloadExcel')}>
            <Button
              variant="outlined"
              icon={<FileExcelOutlined />}
              onClick={() =>
                handleDownloadReport(fileUrl, `${record.name || 'report'}.xlsx`)
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: t('downloadXml'),
      key: 'downloadXml',
      align: 'center',
      width: isMobile ? 90 : 100,
      render: (_, record) => {
        if (!record.xmlReportPath || record.status !== 'COMPLETED') return '—';
        return (
          <Tooltip title={t('downloadXml')}>
            <Button
              variant="outlined"
              icon={<FileTextOutlined />}
              onClick={() =>
                handleDownloadReport(
                  record.xmlReportPath,
                  `${record.name || 'report'}.xml`
                )
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: t('sapStatus'),
      key: 'sapStatus',
      align: 'center',
      width: isMobile ? 110 : 130,
      render: (_, record) => {
        const sapStatus = record.sapStatus;
        if (!sapStatus) return '—';
        const statusConfig = {
          SUCCESS: { color: 'green' },
          PENDING: { color: 'orange' },
          FAILED: { color: 'red' },
          SKIPPED: { color: 'default' },
        };
        const config = statusConfig[sapStatus] || { color: 'default' };
        return (
          <Tag color={config.color} variant="outlined">
            {t(`sap_${sapStatus}`) || sapStatus}
          </Tag>
        );
      },
    },
    {
      title: t('action'),
      key: 'operatorActions',
      align: 'center',
      width: isMobile ? 140 : 180,
      fixed: 'right',
      render: (_, record) => {
        const busy =
          record.status === 'PENDING' || record.status === 'PROCESSING';
        const canSubmit =
          record.status === 'COMPLETED' &&
          !!record.xmlReportPath &&
          (record.sapEmployeeCount == null || record.sapEmployeeCount > 0) &&
          record.sapStatus !== 'SUCCESS';
        return (
          <Space size={4} wrap>
            <Tooltip title={t('regenerateReport')}>
              <Button
                variant="outlined"
                icon={<ReloadOutlined />}
                disabled={busy}
                loading={isRegeneratingReport && actionReportId === record._id}
                onClick={() => {
                  setActionReportId(record._id);
                  regenerateMonthlyReport(
                    { reportId: record._id },
                    { onSettled: () => setActionReportId(null) }
                  );
                }}
              />
            </Tooltip>
            <Tooltip
              title={
                record.sapStatus === 'SUCCESS'
                  ? t('sapAlreadySubmitted')
                  : !record.xmlReportPath
                    ? t('xmlMissingRegenerate')
                    : record.sapEmployeeCount === 0
                      ? t('sapNoLines')
                      : t('submitToSap')
              }
            >
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                disabled={busy || !canSubmit}
                loading={isSubmittingSap && actionReportId === record._id}
                onClick={() => {
                  setActionReportId(record._id);
                  submitSapMonthlyReport(
                    { reportId: record._id },
                    { onSettled: () => setActionReportId(null) }
                  );
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const columns = [
    {
      title: t('reportName'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
    {
      title: t('operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
    ...commonColumns,
  ];

  // eslint-disable-next-line no-unused-vars -- kept if Super Admin tab is restored
  const superAdminColumns = [
    {
      title: t('reportName'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
    ...commonColumns,
  ];

  const residentColumns = [
    {
      title: `${t('resident')}`,
      key: 'reportNameResident',
      align: 'center',
      width: isMobile ? 140 : 200,
      render: (_, record) => {
        const c = record?.customerDetails;
        const residentName = c ? [c.fname, c.lname].filter(Boolean).join(' ').trim() || '—' : '—';
        const label = `${residentName}`;
        const customerId =
          record?.customerId ||
          record?.customerDetails?._id ||
          record?.customerDetails?.id;
        if (customerId) {
          return (
            <Tooltip title={t('viewDetails')}>
              <Button
                type="link"
                variant="outlined"
                className="hover:underline font-medium"
                onClick={() => {
                  setSelectedCustomerIdForDrawer({
                    id: customerId,
                    operatorId: record?.operatorId ?? operatorId,
                  });
                  setCustomerDrawerOpen(true);
                }}
              >
                {label}
              </Button>
            </Tooltip>
          );
        }
        return label;
      },
    },
    {
      title: t('operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      align: 'center',
      width: isMobile ? 120 : 150,
      render: (name, record) => name || record.operatorId || '—',
    },
    ...commonColumns,
  ];

  const customReportColumns = useMemo(() => [
    {
      title: t('reportName'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: isMobile ? 150 : 200,
      ellipsis: true,
      render: (text) => (
        <span className="whitespace-nowrap" title={text}>
          {text || '-'}
        </span>
      ),
    },
    {
      title: t('reportTemplate'),
      dataIndex: 'reportTemplate',
      key: 'reportTemplate',
      align: 'center',
      width: isMobile ? 140 : 180,
      render: (template) => (
        <Tag color="blue" className="whitespace-nowrap" variant="outlined">
          {t(template) || template}
        </Tag>
      ),
    },
    {
      title: t('range'),
      key: 'dateRange',
      align: 'center',
      width: isMobile ? 180 : 220,
      render: (_, record) => {
        const from = record.from;
        const to = record.to;
        if (from == null || to == null) return '-';
        try {
          const isUnixFrom = typeof from === 'number' || (typeof from === 'string' && /^\d+$/.test(from));
          const isUnixTo = typeof to === 'number' || (typeof to === 'string' && /^\d+$/.test(to));
          const f = isUnixFrom ? dayjs.unix(Number(from)) : dayjs(from);
          const tDate = isUnixTo ? dayjs.unix(Number(to)) : dayjs(to);
          let text;
          if (f.month() === tDate.month() && f.year() === tDate.year()) {
            text = `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
          } else if (f.year() === tDate.year()) {
            text = `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
          } else {
            text = `${f.format('DD.MM.YYYY')} - ${tDate.format('DD.MM.YYYY')}`;
          }
          return <span className="whitespace-nowrap">{text}</span>;
        } catch {
          return '-';
        }
      },
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: isMobile ? 100 : 120,
      render: (status) => {
        const statusConfig = {
          COMPLETED: { color: 'green', icon: <CheckCircleOutlined /> },
          PENDING: { color: 'orange', icon: <ClockCircleOutlined /> },
          PROCESSING: { color: 'blue', icon: <SyncOutlined spin /> },
          IN_PROGRESS: { color: 'blue', icon: <SyncOutlined spin /> },
          FAILED: { color: 'red', icon: <CloseCircleOutlined /> },
        };
        const config = statusConfig[status] || { color: 'default', icon: null };
        const label =
          status === 'PROCESSING' ? t('PROCESSING') || t('IN_PROGRESS') : t(status) || status;
        return (
          <Tag color={config.color} icon={config.icon} className="whitespace-nowrap" variant="outlined">
            {label}
          </Tag>
        );
      },
    },
    {
      title: t('createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      align: 'center',
      width: isMobile ? 140 : 180,
      render: (timestamp) => {
        if (timestamp == null) return '-';
        const ts = Number(timestamp);
        if (Number.isNaN(ts)) return '-';
        try {
          return (
            <span className="whitespace-nowrap">
              {dayjs.unix(ts).format('DD.MM.YYYY HH:mm')}
            </span>
          );
        } catch {
          return '-';
        }
      },
    },
    {
      title: t('action'),
      key: 'action',
      align: 'center',
      width: isMobile ? 100 : 120,
      fixed: 'right',
      render: (_, record) =>
        record.status === 'COMPLETED' && (record.excelReportPath || record.reportPath) ? (
          <Tooltip title={t('downloadReport')}>
            <Button
              variant="outlined"
              icon={<DownloadOutlined />}
              loading={downloadingCustomReportId === record._id}
              onClick={() => handleDownloadCustomReport(record)}
            />
          </Tooltip>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
  ], [t, isMobile, downloadingCustomReportId, handleDownloadCustomReport]);

  // Main tab items
  const mainTabItems = useMemo(() => {
    const items = [];

    items.push({
      key: 'reports',
      label: (
        <span>
          <FileTextOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
          {t('reports')}
        </span>
      ),
      children: (
        <Row gutter={18} className="">
          <Col span={24}>
            {/* MonthPicker and Create report for mobile (Create only on Company tab) */}
            {isMobile && (
              <div className="mb-4 flex gap-2 items-center">
                <MonthPicker
                  onChange={handleMonthChange}
                  placeholder={t('selectMonth')}
                  value={selectedMonth ? dayjs(selectedMonth) : null}
                  className="flex-1"
                />
                {activeReportTab === 'OPERATOR' && (
                  <Button
                    variant="outlined"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleOpenCreateReport}
                    className="border-blue-600 text-blue-600 hover:bg-blue-50 hover:border-blue-700 hover:text-blue-700"
                  >
                    {t('createNewReport')}
                  </Button>
                )}
              </div>
            )}

            <Tabs
              activeKey={activeReportTab}
              tabBarExtraContent={!isMobile ? (
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <MonthPicker
                    onChange={handleMonthChange}
                    placeholder={t('selectMonth')}
                    value={selectedMonth ? dayjs(selectedMonth) : null}
                    size="middle"
                    className="max-w-sm"
                  />
                  {activeReportTab === 'CUSTOMER' && (
                    <>
                      <DebouncedSelect
                        value={reportCustomerId}
                        onChange={setReportCustomerId}
                        allowClear
                        className="min-w-[200px]"
                        disabled={!operatorId}
                        fetchOptions={(search) => (operatorId ? fetchCustomerList({ operatorId, search }) : Promise.resolve([]))}
                        placeholder={operatorId ? t('searchCustomer') : t('selectOperatorFirst')}
                        loadOnOpen
                      />
                      {reportCustomerId && (
                        <Button
                          variant="outlined"
                          size="middle"
                          onClick={() => setReportCustomerId(null)}
                        >
                          {t('clear')}
                        </Button>
                      )}
                      {['SUPER_ADMIN', 'OPERATOR'].includes(user?.role) && (
                        <Button
                          type="primary"
                          size="middle"
                          loading={isGeneratingCustomerPdf}
                          disabled={!reportCustomerId || !selectedMonth}
                          onClick={handleGenerateCustomerPdf}
                        >
                          {t('generateCustomerPdf')}
                        </Button>
                      )}
                    </>
                  )}
                  {activeReportTab === 'OPERATOR' && (
                    <Button
                      variant="outlined"
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleOpenCreateReport}
                      className="border-blue-600 text-blue-600 hover:bg-blue-50 hover:border-blue-700 hover:text-blue-700"
                    >
                      {t('createNewReport')}
                    </Button>
                  )}
                </div>
              ) : undefined}
              onChange={handleReportsTabChange}
              items={[
                ...(['SUPER_ADMIN', 'ADMIN', 'OPERATOR'].includes(user?.role)
                  ? [
                    {
                      key: 'OPERATOR',
                      label: (
                        <span>
                          <TeamOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                          {t('operators')}
                        </span>
                      ),
                      children: (
                        <>
                          <Table
                            columns={operatorColumns}
                            dataSource={reportList?.paginatedData}
                            loading={isReportListLoading}
                            rowKey="_id"
                            pagination={{
                              ...pagination,
                              pageSize: isMobile ? 5 : pagination.pageSize,
                              position: ['bottomCenter'],
                              total: reportList?.totalCount || 0,
                              size: isMobile ? 'small' : 'default',
                            }}
                            onChange={handleTableChange}
                            scroll={{ x: isMobile ? 1100 : 1400 }}
                          />
                        </>
                      ),
                    },
                  ]
                  : []),
                {
                  key: 'CUSTOMER',
                  label: (
                    <span>
                      <UserOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('residentUtilizationReport')}
                    </span>
                  ),
                  children: (
                    <>
                      {isMobile && activeReportTab === 'CUSTOMER' && (
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                          <DebouncedSelect
                            value={reportCustomerId}
                            onChange={setReportCustomerId}
                            allowClear
                            className="w-full min-w-0"
                            disabled={!operatorId}
                            fetchOptions={(search) => (operatorId ? fetchCustomerList({ operatorId, search }) : Promise.resolve([]))}
                            placeholder={operatorId ? t('searchCustomer') : t('selectOperatorFirst')}
                            loadOnOpen
                          />
                          {reportCustomerId && (
                            <Button
                              variant="outlined"
                              size="middle"
                              onClick={() => setReportCustomerId(null)}
                            >
                              {t('clear')}
                            </Button>
                          )}
                        </div>
                      )}
                      <Table
                        columns={residentColumns}
                        dataSource={reportList?.paginatedData}
                        loading={isReportListLoading}
                        rowKey="_id"
                        pagination={{
                          ...pagination,
                          pageSize: isMobile ? 5 : pagination.pageSize,
                          position: ['bottomCenter'],
                          total: reportList?.totalCount || 0,
                          size: isMobile ? 'small' : 'default',
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: isMobile ? 800 : undefined }}
                      />
                    </>
                  ),
                },
              ]}
            />
          </Col>
        </Row>
      ),
    });

    if (user?.role !== 'LOCATION_OWNER') {
      items.push({
        key: 'otherReports',
        label: (
          <span>
            <LineChartOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
            {t('otherReports')}
          </span>
        ),
        children: (
          <>
            <Tabs
              activeKey={activeOtherReportTab}
              onChange={handleOtherReportsTabChange}
              size="small"
              items={[
                {
                  key: 'chargePointCount',
                  label: (
                    <span>
                      <ThunderboltOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('chargePointCount')}
                    </span>
                  ),
                  children: (
                    <ChargePointCountTab
                      operatorId={operatorId}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'chargePointCount'}
                    />
                  ),
                },
                {
                  key: 'chargingSession',
                  label: (
                    <span>
                      <PoweroffOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('chargingSession')}
                    </span>
                  ),
                  children: (
                    <ChargingSessionTab
                      operatorId={operatorId}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'chargingSession'}
                    />
                  ),
                },
                {
                  key: 'energyConsumption',
                  label: (
                    <span>
                      <LineChartOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('energyConsumption')}
                    </span>
                  ),
                  children: (
                    <EnergyConsumptionTab
                      operatorId={operatorId}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'energyConsumption'}
                    />
                  ),
                },
                {
                  key: 'chargerOnlineTime',
                  label: (
                    <span>
                      <ClockCircleOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('chargerOnlineTime')}
                    </span>
                  ),
                  children: (
                    <ChargerOnlineTimeTab
                      operatorId={operatorId}
                      operatorName={operatorName}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'chargerOnlineTime'}
                    />
                  ),
                },
                {
                  key: 'userCount',
                  label: (
                    <span>
                      <UserOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('userCount')}
                    </span>
                  ),
                  children: (
                    <UserCountTab
                      operatorId={operatorId}
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'userCount'}
                    />
                  ),
                },
                {
                  key: 'userAnalysis',
                  label: (
                    <span>
                      <UserSwitchOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                      {t('userAnalysis')}
                    </span>
                  ),
                  children: (
                    <UserAnalysisTab
                      active={activeMainTab === 'otherReports' && activeOtherReportTab === 'userAnalysis'}
                    />
                  ),
                },
              ]}
            />
          </>
        ),
      });
    }

    items.push({
      key: 'customReports',
      label: (
        <span>
          <FileTextOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
          {t('customReports')}
        </span>
      ),
      children: (
        <div className="custom-reports-table-wrapper space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <RangePicker
              value={customReportDateRange}
              onChange={setCustomReportDateRange}
              placeholder={[t('startDate'), t('endDate')]}
              style={{ width: 250 }}
            />
            <Select
              placeholder={t('selectStatus')}
              style={{ width: 150 }}
              allowClear
              value={customReportStatus}
              onChange={setCustomReportStatus}
            >
              <Option value="PENDING">{t('PENDING')}</Option>
              <Option value="IN_PROGRESS">{t('IN_PROGRESS')}</Option>
              <Option value="COMPLETED">{t('COMPLETED')}</Option>
              <Option value="FAILED">{t('FAILED')}</Option>
            </Select>
            <Select
              placeholder={t('selectTemplate')}
              style={{ width: 200 }}
              allowClear
              value={customReportTemplate}
              onChange={setCustomReportTemplate}
            >
              <Option value="CHARGING_HISTORY">{t('CHARGING_HISTORY')}</Option>
              <Option value="CHARGING_SESSION">{t('CHARGING_SESSION')}</Option>
              <Option value="RFID_USAGE_REPORT">{t('RFID_USAGE_REPORT')}</Option>
            </Select>
            <Button
              variant="outlined"
              icon={<ReloadOutlined />}
              onClick={refetchCustomReportList}
              loading={isCustomReportListLoading}
            >
              {t('Refresh')}
            </Button>
          </div>
          <style>{`
            .custom-reports-table-wrapper .ant-pagination {
              display: flex !important;
              justify-content: center !important;
              align-items: center !important;
              flex-wrap: nowrap !important;
              gap: 8px !important;
            }
            .custom-reports-table-wrapper .ant-pagination > * {
              flex-shrink: 0 !important;
            }
          `}</style>
          <Table
            columns={customReportColumns}
            dataSource={customReportList?.paginatedData || []}
            loading={isCustomReportListLoading}
            rowKey="_id"
            variant="outlined"
            size={isMobile ? 'small' : 'default'}
            scroll={{ x: isMobile ? 800 : 'max-content' }}
            pagination={{
              current: customReportPagination.current,
              pageSize: customReportPagination.pageSize,
              total: customReportList?.totalCount || 0,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20', '50'],
              position: ['bottomCenter'],
              size: 'small',
              showTotal: false,
              onChange: (page, pageSize) => {
                setCustomReportPagination({
                  current: page,
                  pageSize: pageSize,
                  total: customReportList?.totalCount || 0,
                });
              },
              onShowSizeChange: (current, size) => {
                setCustomReportPagination({
                  current: 1,
                  pageSize: size,
                  total: customReportList?.totalCount || 0,
                });
              },
            }}
            locale={{
              emptyText: (
                <Empty
                  description={t('noCustomReports')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        </div>
      ),
    });

    return items;
  }, [user?.role, t, activeReportTab, operatorId, operatorName, commonColumns, operatorColumns, reportList?.paginatedData, reportList?.totalCount, isReportListLoading, pagination, columns, activeOtherReportTab, activeMainTab, customReportList, isCustomReportListLoading, customReportPagination, isMobile, customReportDateRange, customReportStatus, customReportTemplate]);

  // Handle navigation state
  useEffect(() => {
    if (location.state?.activeMainTab) {
      setActiveMainTab(location.state.activeMainTab);
    }
    if (location.state?.activeOtherReportTab) {
      setActiveOtherReportTab(location.state.activeOtherReportTab);
    }
  }, [location.state]);

  return (
    <div className="space-y-4">
      <Tabs
        activeKey={activeMainTab}
        onChange={handleMainTabChange}
        tabBarExtraContent={
          // Remove extra reload button as it is now integrated in the tab content
          undefined

        }
        items={mainTabItems}
      />

      <Drawer
        title={
          <div>
            <Title level={4} className="mb-0!">
              {t('createNewReport')}
            </Title>
            <Paragraph type="secondary" className="mb-0! mt-1! text-sm">
              {t('createReportSubtitle')}
            </Paragraph>
          </div>
        }
        placement="right"
        styles={{
          wrapper: {
            width: Math.min(1120, typeof window !== 'undefined' ? window.innerWidth * 0.94 : 1120),
          },
          body: { paddingTop: 12, background: '#fafafa' },
        }}
        onClose={handleCloseCreateReportDrawer}
        open={createReportDrawerOpen}
        destroyOnClose
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outlined" onClick={handleCloseCreateReportDrawer}>
              {t('cancel')}
            </Button>
            <Tooltip title={!canCreateReport ? createReportDisabledReason : undefined}>
              <Button
                type="primary"
                loading={isGeneratingReport}
                disabled={!canCreateReport}
                onClick={() => createReportForm.submit()}
              >
                {t('createReportAction')}
              </Button>
            </Tooltip>
          </div>
        }
      >
        <Form
          form={createReportForm}
          layout="vertical"
          size="middle"
          onFinish={handleCreateReportSubmit}
          initialValues={{ reportMonth: dayjs().subtract(1, 'month') }}
        >
          <CreateReportStep
            step={1}
            title={t('createReportStep1Title')}
            description={t('createReportStep1Desc')}
          >
            <Form.Item
              name="reportMonth"
              rules={[{ required: true, message: t('selectMonthFirst') }]}
              extra={t('createReportOverwriteHintShort')}
              className="mb-0!"
            >
              <MonthPicker
                className="w-full max-w-md"
                allowClear={false}
                format="MMMM YYYY"
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
                disabledDate={(current) =>
                  current && current.isAfter(dayjs(), 'month')
                }
                onChange={(date) => {
                  const next = date || dayjs().subtract(1, 'month');
                  setCreateReportMonth(next);
                  createReportForm.setFieldsValue({ reportMonth: next });
                  setTouCoverage({
                    isComplete: false,
                    status: 'incomplete',
                    isLoading: true,
                    isSaving: false,
                    dirty: false,
                  });
                }}
                placeholder={t('selectMonth')}
              />
            </Form.Item>
          </CreateReportStep>

          <CreateReportStep
            step={2}
            title={t('createReportStep2Title')}
            description={t('createReportStep2Desc')}
            extra={touToolbar}
          >
            <TouInlineEditor
              key={`${operatorId}-${createTouYear}-${createTouMonth}`}
              operatorId={operatorId}
              year={createTouYear}
              month={createTouMonth}
              onCoverageChange={handleTouCoverageChange}
              onToolbarChange={setTouToolbar}
            />
          </CreateReportStep>

          <CreateReportStep
            step={3}
            title={t('createReportStep3Title')}
            description={t('createReportStep3Desc')}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="fullBillAmount"
                  label={t('fullBillAmount')}
                  extra={t('fullBillAmountHelp')}
                  className="mb-0!"
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    className="w-full!"
                    placeholder={t('placeholderFullBillAmount')}
                    addonAfter={currency}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t('billFileOptional')} className="mb-0!">
                  <Dragger
                    fileList={createReportBillFileList}
                    maxCount={1}
                    customRequest={handleBillFileUpload}
                    showUploadList
                    onRemove={() => {
                      setCreateReportUploadedBillUrl('');
                      setCreateReportBillFileList([]);
                    }}
                    onChange={({ fileList }) =>
                      setCreateReportBillFileList(fileList.slice(-1))
                    }
                    accept=".pdf,.xlsx,.xls,.doc,.docx,image/*"
                    className="bg-white!"
                  >
                    <p className="ant-upload-drag-icon mb-2!">
                      <CloudUploadOutlined
                        style={{ color: 'var(--ant-color-primary)', fontSize: 28 }}
                      />
                    </p>
                    <p className="ant-upload-text text-sm!">
                      {t('billFileDropHint')}{' '}
                      <Text style={{ color: 'var(--ant-color-primary)' }}>
                        {t('uploadBillFile')}
                      </Text>
                    </p>
                    <p className="ant-upload-hint text-xs!">{t('billFileSupports')}</p>
                  </Dragger>
                </Form.Item>
              </Col>
            </Row>
          </CreateReportStep>
        </Form>
      </Drawer>

      {customerDrawerOpen && selectedCustomerIdForDrawer?.id && (
        <CustomerDrawerView
          open={customerDrawerOpen}
          onClose={() => {
            setCustomerDrawerOpen(false);
            setSelectedCustomerIdForDrawer(null);
          }}
          id={selectedCustomerIdForDrawer.id}
          operatorId={selectedCustomerIdForDrawer.operatorId}
        />
      )}
    </div>
  );
}
